import { Link } from "react-router-dom";
import Seo from "../../components/Seo";

const UPDATED = "February 15, 2026";

export default function PrivacyPolicy() {
  return (
    <div className="bg-stone-50 min-h-screen" data-testid="privacy-page">
      <Seo
        title="Privacy Policy"
        description="Privacy Policy for Gens Realty, LLC and Edge Home Finance — how we collect, use, and protect your information."
        path="/privacy"
      />
      <article className="max-w-4xl mx-auto px-6 lg:px-10 pt-14 pb-24">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#E63946] mb-3">Legal</div>
        <h1 className="font-display text-4xl lg:text-6xl font-light tracking-tighter">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mt-4">Last updated: {UPDATED}</p>

        <div className="mt-10 space-y-8 text-[#333] leading-relaxed">
          <Section title="1. Who we are">
            <p>
              This Privacy Policy describes how <strong>Gens Realty, LLC</strong> ("Gens Realty," "we,"
              "us") collects, uses, and shares information about you when you use{" "}
              <Link to="/" className="text-[#E63946] underline">our website</Link>{" "}
              and related services. References to mortgage products refer to{" "}
              <strong>Edge Home Finance Corporation</strong> (NMLS #891464), a separate company affiliated
              through our Loan Officer Pierre Joseph.
            </p>
          </Section>

          <Section title="2. Information we collect">
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Information you provide:</strong> name, email, phone number, message content,
                property preferences, financial details you submit on the pre-approval form
                (loan type, target price, down payment, income range, credit range, timeline), and
                any messaging-consent choices you make (checkbox opt-ins, timestamps).
              </li>
              <li>
                <strong>Automatically collected:</strong> IP address, browser type, pages visited, referring
                site, device identifiers, and similar analytics signals (via cookies and similar technologies).
              </li>
              <li>
                <strong>Authentication info:</strong> if you sign in to our admin portal via Google,
                we receive your Google email, display name, and profile picture.
              </li>
              <li>
                <strong>Chat and lead-qualification data:</strong> messages you exchange with our
                AI assistant ("Ava"), the intent and score assigned to your inquiry, and any
                properties you save or search filters you save for weekly digest emails.
              </li>
            </ul>
          </Section>

          <Section title="3. How we use it">
            <ul className="list-disc pl-6 space-y-2">
              <li>To respond to property inquiries and showing requests.</li>
              <li>To route mortgage pre-approval requests to Pierre Joseph at Edge Home Finance.</li>
              <li>To improve our website, content, and services.</li>
              <li>To comply with applicable laws (e.g., TREC, RESPA, NMLS, fair housing, TCPA).</li>
              <li>To send you communications you have requested or consented to receive.</li>
            </ul>
          </Section>

          <Section title="4. How we share information">
            <p>
              We <strong>do not sell</strong> your personal information, and we <strong>do not share
              your mobile opt-in data or SMS consent</strong> with third parties or affiliates for
              their own marketing purposes. We may share information with:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                <strong>Edge Home Finance Corporation</strong> when you submit a pre-approval inquiry —
                so a licensed Loan Officer can follow up.
              </li>
              <li>
                <strong>Service providers</strong> we contract with to operate the site — for example,
                hosting (Emergent), analytics (Google Analytics 4), our MLS data feed
                (CoreLogic Trestle / HAR.com IDX), email delivery (SendGrid), SMS delivery (Twilio),
                and AI tooling used by the Ava assistant — under confidentiality obligations
                and only for the purpose of providing those services to us.
              </li>
              <li>
                <strong>Authorities</strong> when legally required, in response to lawful process, or to
                protect our rights, users, or the public.
              </li>
            </ul>
          </Section>

          <Section title="5. SMS / text-message program (TCPA &amp; A2P 10DLC)">
            <p>
              If you provide your mobile number and check the messaging-consent box on a form
              (for example, on our <Link to="/contact" className="text-[#E63946] underline">
              Contact page</Link>), you agree to receive marketing and transactional text messages
              from Gens Realty, LLC at the number provided, sent using an automatic telephone
              dialing system. Consent is <strong>not a condition of any purchase</strong>.
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>Program name:</strong> Gens Realty client updates &amp; property alerts.</li>
              <li><strong>Message frequency:</strong> varies; typically 2–6 messages per month.</li>
              <li>
                <strong>Message &amp; data rates may apply.</strong> Check with your mobile carrier
                for details.
              </li>
              <li>
                <strong>Opt out any time</strong> by replying <strong>STOP</strong> to any message we send.
                For help, reply <strong>HELP</strong> or email{" "}
                <a href="mailto:PJoseph@gensrealty.com" className="text-[#E63946] underline">
                  PJoseph@gensrealty.com
                </a>.
              </li>
              <li>
                Supported carriers include AT&amp;T, T-Mobile, Verizon Wireless, Sprint, Boost,
                U.S. Cellular, MetroPCS, and other major U.S. carriers. Carriers are not liable
                for delayed or undelivered messages.
              </li>
              <li>
                <strong>No sharing:</strong> Mobile opt-in information and consent records are
                <strong> not shared or sold</strong> to third parties or affiliates for their marketing purposes.
              </li>
            </ul>
          </Section>

          <Section title="6. Cookies &amp; analytics">
            <p>
              We use cookies and similar technologies to operate the site, remember preferences, and
              measure usage — including Google Analytics 4 for aggregate traffic reporting. You can
              control cookies via your browser settings. Disabling cookies may affect site functionality.
            </p>
          </Section>

          <Section title="7. Third-party services">
            <p>
              Our pages may embed content from third parties — for example, the HAR.com IDX system,
              the Edge Home Finance 1003 application portal, or map / imagery providers. Their
              privacy practices are governed by their own policies.
            </p>
          </Section>

          <Section title="8. Your choices and rights">
            <p>
              Texas residents (under the <strong>Texas Data Privacy and Security Act</strong>) and
              other applicable users may request to (i) know what personal information we hold about
              them, (ii) correct inaccuracies, (iii) delete their information, or (iv) opt out of
              targeted advertising and the sale of personal data (we do not sell). You may also opt
              out of marketing emails at any time using the unsubscribe link, and out of SMS by
              replying STOP. Email{" "}
              <a href="mailto:PJoseph@gensrealty.com" className="text-[#E63946] underline">
                PJoseph@gensrealty.com
              </a>{" "}
              with any request; we respond within 45 days as required by law.
            </p>
          </Section>

          <Section title="9. Security">
            <p>
              We use reasonable administrative, technical, and physical safeguards to protect your
              information. However, no method of transmission or storage is 100% secure.
            </p>
          </Section>

          <Section title="10. Children's privacy">
            <p>Our services are not directed to children under 13, and we do not knowingly collect data from them.</p>
          </Section>

          <Section title="11. Changes to this policy">
            <p>
              We may update this policy from time to time. The "Last updated" date at the top reflects the
              latest revision. Continued use of the site after changes are posted constitutes acceptance
              of the revised policy.
            </p>
          </Section>

          <Section title="12. Contact us">
            <p>
              Gens Realty, LLC · 14911 Heritage Wood Drive, Houston, TX 77082 ·{" "}
              <a href="mailto:PJoseph@gensrealty.com" className="text-[#E63946] underline">PJoseph@gensrealty.com</a>{" "}
              · (832) 741-4662
            </p>
            <p className="mt-3">
              For mortgage-related privacy questions, contact{" "}
              <strong>Edge Home Finance Corporation</strong> · NMLS #891464 ·{" "}
              <a href="mailto:info@edgehomefinance.com" className="text-[#E63946] underline">
                info@edgehomefinance.com
              </a>{" "}
              · (763) 219-8484.
            </p>
          </Section>
        </div>
      </article>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-semibold text-[#1A1A1A] mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
